import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldX } from 'lucide-react';

import type { ModerationItem } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';

import { ReasonConfirm, type ConfirmMode } from './reason-confirm';
import { useDecideModeration } from './use-decide-moderation';

/**
 * Approve, or reject with a reason, on the review detail screen.
 *
 * The same decision call and the same `ReasonConfirm` the queue lists use, so an admin
 * meets one flow wherever they decide. On success the caller is told, and it leaves the
 * screen.
 */
export function ModerationDecision({
  item,
  name,
  onDecided,
  wording,
}: {
  item: ModerationItem;
  /** What the confirmation names: the organization, or the credential's title. */
  name: string;
  onDecided: (decision: 'approve' | 'reject') => void;
  /** Words for an item the subject's defaults do not describe — a video introduction. */
  wording?: { rejectAction: string; approveBody: string; rejectHint: string; rejected: string };
}) {
  const subject = item.subject;
  const t = useTranslations(subject === 'organization' ? 'admin.verification' : 'admin.documents');
  const [mode, setMode] = useState<ConfirmMode | null>(null);

  const mutation = useDecideModeration(
    subject,
    ({ decision }) => onDecided(decision),
    () => wording?.rejected,
  );
  const toggle = (next: ConfirmMode) => setMode((current) => (current === next ? null : next));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => toggle('approve')} className="gap-1.5">
          <ShieldCheck aria-hidden className="h-4 w-4" />
          {t('approve')}
        </Button>
        <Button
          type="button"
          variant="danger-soft"
          onClick={() => toggle('reject')}
          className="gap-1.5"
        >
          <ShieldX aria-hidden className="h-4 w-4" />
          {wording?.rejectAction ?? t('reject.action')}
        </Button>
      </div>

      {mode && (
        <ReasonConfirm
          key={mode}
          subject={subject}
          mode={mode}
          name={name}
          pending={mutation.isPending}
          onApprove={() => mutation.mutate({ itemId: item.id, decision: 'approve' })}
          onReject={(reason) => mutation.mutate({ itemId: item.id, decision: 'reject', reason })}
          onCancel={() => setMode(null)}
          approveBody={wording?.approveBody}
          rejectHint={wording?.rejectHint}
        />
      )}
    </div>
  );
}
